import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";

import { FETCH_ENABLED_WIDGETS } from "../../api/widgets";
import LoadingScreen from "../../components/LoadingScreen";
import DynamicComponent from "./components/index";
import { UseUserDetails } from "../../hooks/auth";
import { GET_USER_BY_GROUPID } from "../../api/user";
import { GET_WIDGETS_LIST } from "../../api/userWidgets";

// Helper function: Sort by toggled status and then alphabetically by title
const sortWidgets = (widgetsList) => {
  const widgetsCopy = [...widgetsList];
  return widgetsCopy
    .sort((a, b) => {
      // Active (toggled=true) first
      if (a.toggled !== b.toggled) {
        return b.toggled - a.toggled; // true (active) before false (inactive)
      }
      // Then alphabetically by title
      if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
      if (a.title.toLowerCase() > b.title.toLowerCase()) return 1;
      return 0;
    });
};

const WidgetsList = ({ searchTerm }) => {
  const [widgets, setWidgets] = useState([]);
  const [filteredWidgets, setFilteredWidgets] = useState([]);
  const [reFetch, setReFetch] = useState(0);
  const [fetchingWidgets, setFetchingWidgets] = useState(false);
  const userDetails = UseUserDetails();
  const groupId = userDetails?.groupId;
  
  // Function to fetch the list of enabled widgets and apply necessary filters
  const fetchEnabledWidgets = async () => {
    setFetchingWidgets(true);
    try {
      const enabledWidgetsPromise = FETCH_ENABLED_WIDGETS();
      let groupUsersPromise = null;
      let groupWidgetsPromises = [];

      let shouldFetchGroupWidgets = false;
      if (groupId) {
        groupUsersPromise = GET_USER_BY_GROUPID(groupId);
        shouldFetchGroupWidgets = true;
      }

      const { data } = await enabledWidgetsPromise;
      let updatedWidgets = [];
      const isAdmin = userDetails?.isAdmin === true;
      const createdAt = userDetails?.createdAt;
      const accountCreationDate = new Date(createdAt);
      const cutoffDate = new Date('2025-06-01');
      const isNewAdmin = accountCreationDate >= cutoffDate;
      let filteredData = [];

      if (isAdmin && isNewAdmin) {
        filteredData = data.filter(widget => !["Quote of the Day", "Ranking", "Level", "Top KPI", "Bonus Estimate"].includes(widget.title));
      } else {
        filteredData = data.filter(widget => widget.title !== "Quote of the Day");
      }

      const toggledSet = new Set((userDetails.widgets || []).map(w => w.key));
      updatedWidgets = filteredData.map(widget => ({
        ...widget,
        toggled: toggledSet.has(widget.key),
      }));

      if (shouldFetchGroupWidgets) {
        try {
          const { data: groupUsersRaw } = await groupUsersPromise;
          const filteredUsers = groupUsersRaw.filter(
            user => !user.isArchived && user.companyId !== null && user.logins > 0
          );
          if (filteredUsers?.length) {
            const filteredUserIds = new Set(filteredUsers.map(user => user._id));
            const widgetKeysToUpdate = ["player_memo", "my_level"];
            groupWidgetsPromises = widgetKeysToUpdate.map(key =>
              GET_WIDGETS_LIST({
                key,
                companyId: null,
                userId: null,
              }).then(({ data: groupWidgets = [] }) => ({ key, groupWidgets }))
            );
            const widgetsListResults = await Promise.all(groupWidgetsPromises);

            widgetsListResults.forEach(({ key, groupWidgets }) => {
              if (
                groupWidgets.length &&
                groupWidgets.some(widgetItem => filteredUserIds.has(widgetItem.userId))
              ) {
                updatedWidgets = updatedWidgets.map(w =>
                  w.key === key ? { ...w, toggled: true } : w
                );
              }
            });
          }
        } catch (specialWidgetError) {
          console.error("Error updating special widgets for group:", specialWidgetError);
        }
      }

      // Sort the widgets before saving to state
      const sortedWidgets = sortWidgets(updatedWidgets);

      setWidgets(sortedWidgets);
      setFilteredWidgets(sortedWidgets);
    } catch (error) {
      console.log(error);
    }
    setFetchingWidgets(false);
  };

  const fetchEnabledWidgetsWithoutLoading = async () => {
    fetchEnabledWidgets();
  };

  useEffect(() => {
    fetchEnabledWidgets();
  }, []);

  useEffect(() => {
    if (reFetch === 1 || reFetch === 2) {
      fetchEnabledWidgetsWithoutLoading();
      setReFetch(0);
    }
  }, [reFetch]);

  useEffect(() => {
    const filtered = widgets.filter((widget) =>
      widget.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // Sort the filtered widgets before setting filteredWidgets
    setFilteredWidgets(sortWidgets(filtered));
  }, [searchTerm, widgets]);

  return fetchingWidgets ? (
    <LoadingScreen />
  ) : (
    <Grid container spacing={3}>
      {filteredWidgets.map((widget) => (
        <Grid
          item
          mini={12}
          xs={6}
          md={4}
          lg={3}
          xl={4}
          xxl={3}
          xxxl={3}
          key={widget.key}
        >
          <DynamicComponent
            widget={widget}
            reFetch={reFetch}
            setReFetch={setReFetch}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default WidgetsList;
